import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTH_SERVER_BASE_URL } from '../../constants/platform';
import { SYSTEM_ROLES } from '../../constants/system';
import { AISearch, Header } from '../../lib/magnetosphere';
import { SerializeMode } from '../../lib/magnetosphere-types';
import type { TabType } from '../../types/tab';
import type { UserTenantResponseAPI } from '../../types/user';
import {
    getAccessToken,
    getAccountId,
    getAuthFromWindow,
    getErrorMessage,
    switchAccount,
} from '../../utils/auth';
import { FeatureFlag } from '../../utils/featureFlags';
import { generateRouteUrl } from '../../utils/routing';
import { useFeatureFlag } from '../featureFlags/FeatureFlagProvider';
import { buildMastheadSubNav, MASTHEAD_NAV_IDS, MASTHEAD_TAB_NAV_IDS } from './MastheadNav';

const pageInfo = {
    name: 'Flow',
    homePage: { url: '/', title: 'Flow' },
    redirect: { url: '/', title: 'Flow' },
};

const isLocalHost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

interface PlatformMastheadProps {
    tenant: UserTenantResponseAPI | null;
    openTenantSelector: () => void;
}

const PlatformMasthead = ({ tenant, openTenantSelector }: PlatformMastheadProps) => {
    const navigate = useNavigate();
    const [jwt, setJwt] = useState('');
    const [accountMode, setAccountMode] = useState(false);
    const [isAiSearchOpen, setIsAiSearchOpen] = useState(false);

    useLayoutEffect(() => {
        getAccessToken().then((token) => {
            if (token) {
                setJwt(token);
            }
        });
    }, []);

    const { getFlag } = useFeatureFlag();
    const isOrgAccEnabled = getFlag(FeatureFlag.ORGACC);

    useEffect(() => {
        const isSingleTenant = !!(
            isOrgAccEnabled &&
            tenant?.organization?.accountId &&
            tenant?.organization?.tenantCount === 1
        );
        setAccountMode(isSingleTenant);
    }, [isOrgAccEnabled, tenant?.organization?.accountId, tenant?.organization?.tenantCount]);

    let isAdministrator = tenant?.role === SYSTEM_ROLES.administrator.developerName;
    if (
        accountMode &&
        (tenant?.role === SYSTEM_ROLES.administrator.developerName ||
            tenant?.organization?.role === 'organization_administrator')
    ) {
        isAdministrator = true;
    }

    const showEnvironmentMenuItem = Boolean(
        tenant?.tenantSettings?.environments && isAdministrator,
    );

    const onNavigation = useCallback(
        (menu?: { id?: string }) => {
            if (menu?.id === MASTHEAD_NAV_IDS.switchTenant) {
                openTenantSelector();
                return;
            }
            if (menu?.id && MASTHEAD_TAB_NAV_IDS.includes(menu.id)) {
                navigate(generateRouteUrl({ tabType: menu.id as TabType }));
            }
        },
        [navigate, openTenantSelector],
    );

    const signOut = useCallback(async () => {
        try {
            const auth = getAuthFromWindow();
            if (auth?.isAuthenticated()) {
                await auth.logout();
            }
        } catch (err) {
            console.error(getErrorMessage(err));
        }
    }, []);

    const handleSignOut = useCallback(
        async (logoutRedirectUrl?: string) => {
            cookieStore.delete('ACCOUNT_ID');
            await signOut();
            window.location.assign(logoutRedirectUrl || '/');
        },
        [signOut],
    );

    const onSignOutSuccess = useCallback(
        (_signOutType: string, logoutRedirectUrl: string) => {
            handleSignOut(logoutRedirectUrl);
        },
        [handleSignOut],
    );

    const routeToAi = useCallback((isClickedAi: boolean) => {
        if (!isClickedAi) return;
        getAccountId().then((accountId) => {
            if (accountId) {
                window.open(
                    `${AUTH_SERVER_BASE_URL.replace(/\/$/, '')}/BoomiAI.html#ai;accountId=${accountId}`,
                    '_blank',
                );
            }
        });
    }, []);

    const onAuthError = useCallback(() => {
        cookieStore.delete('ACCOUNT_ID');
        navigate('/');
    }, [navigate]);

    return (
        <>
            <Header
                key={
                    accountMode +
                    (showEnvironmentMenuItem ? 'with-environments' : 'without-environments')
                }
                pageInfo={pageInfo}
                checkForDevelopment={isLocalHost}
                platformUrl={AUTH_SERVER_BASE_URL}
                usePlatformUrlAsHost
                jwt={jwt}
                subNav={buildMastheadSubNav({
                    currentTenantName: tenant?.developerName,
                    isAdministrator,
                    showEnvironmentMenuItem,
                    accountMode: accountMode,
                    hasTenant: tenant !== null,
                })}
                onNavigation={onNavigation}
                onSwitchAccountSuccess={({ accountId }) => switchAccount({ accountId })}
                onSignOutSuccess={onSignOutSuccess}
                onAuthError={onAuthError}
                showBoomiAi={routeToAi}
                searchIconEventHandler={() => setIsAiSearchOpen((prev) => !prev)}
                disableActiveNavHighlight
                disableMoreMenu
                disableTranslation
                suppressDefaultLandingPage
                noNeedAuthorization
            />
            <AISearch
                key={isAiSearchOpen ? 'open' : 'closed'}
                openSearchPanel={isAiSearchOpen}
                userIsAuthenticated
                closeBtnEventHandler={() => setIsAiSearchOpen(false)}
                jwt={jwt}
                serializeMode={SerializeMode.BACKEND}
            />
        </>
    );
};

export default PlatformMasthead;
